import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberInvoice,
  useQueryGetInvoiceByInvoiceNumber,
} from "@gymflow/api";
import { formatCurrency } from "@gymflow/helpers";
import {
  Button,
  ChangeCalculator,
  CloseIcon,
  FormPaymentMethodPicker,
  FormSelectCustomPaymentCategory,
  FormTabs,
  Spinner,
  TabType,
} from "apps/portal/src/components/atoms";
import DashedIcon from "apps/portal/src/components/atoms/icons/DashedIcon";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidationSchema } from "zod-formik-adapter";

import {
  collectPastDueInvoiceFormSchema,
  INITIAL_VALUES_COLLECT_PAST_DUE_INVOICE_FORM,
  TABS_COLLECT_PAST_DUE_INVOICE,
} from "./constants";
import {
  CollectPastDueInvoiceFormProps,
  CollectPastDueInvoiceFormValues,
  TabsCollectPastDueInvoiceType,
} from "./types";

export const CollectPastDueInvoiceForm = ({
  invoiceNumber,
  userMemberId,
  onClose,
  title,
}: CollectPastDueInvoiceFormProps) => {
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const { api } = useGymflowModels();
  const [parent] = useAutoAnimate();

  const { data: invoice, isFetching: isFetchingGetInvoiceByInvoiceNumber } =
    useQueryGetInvoiceByInvoiceNumber(
      {
        api,
        invoiceNumber,
      },
      { enabled: !!invoiceNumber },
    );
  const {
    collectPastDueInvoiceMutation,
    markCollectedByCustomPaymentMutation,
  } = useMemberInvoice({ api, memberId: userMemberId });

  const customPaymentsEnabled = club?.customPaymentsEnabled;
  const showScaModal = useScaModal({
    asMember: false,
  });
  const formik = useFormik<CollectPastDueInvoiceFormValues>({
    initialValues: INITIAL_VALUES_COLLECT_PAST_DUE_INVOICE_FORM,
    validationSchema: toFormikValidationSchema(collectPastDueInvoiceFormSchema),
    onSubmit: async ({
      activeTabId,
      inPersonPaymentMethod,
      paymentMethodId,
    }: CollectPastDueInvoiceFormValues) => {
      if (activeTabId === "ONLINE" && paymentMethodId) {
        await collectPastDueInvoiceMutation.mutateAsync({
          invoiceNumber,
          paymentMethodId,
        });
        await showScaModal({
          invoiceNumber,
          userMemberId,
        });
        onCloseSidebar();
      } else if (
        activeTabId === "IN_PERSON" &&
        inPersonPaymentMethod?.value.id
      ) {
        await markCollectedByCustomPaymentMutation.mutateAsync({
          invoiceNumber,
          customPaymentCategoryId: inPersonPaymentMethod.value.id,
        });
        onCloseSidebar();
      }
    },
  });

  const onCloseSidebar = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [formik, onClose]);

  const tabs = useMemo((): TabType<TabsCollectPastDueInvoiceType>[] => {
    const baseTabs: TabType<TabsCollectPastDueInvoiceType>[] =
      TABS_COLLECT_PAST_DUE_INVOICE;

    return baseTabs.filter((tab) => {
      if (tab.id === "IN_PERSON" && !customPaymentsEnabled) {
        return false;
      }
      return true;
    });
  }, [customPaymentsEnabled]);

  const { inPersonPaymentMethod, activeTabId } = formik.values;

  const dueAmount = invoice?.dueAmount ?? 0;

  return (
    <FormikProvider value={formik}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col justify-between border-b border-b-gray-300 p-6 pb-5">
            <div className="mb-1 flex flex-row items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">
                {title ?? t("components.collectPastDueInvoiceSidebar.title")}
              </div>
              <div className="cursor-pointer" onClick={() => onCloseSidebar()}>
                <CloseIcon className="size-6" />
              </div>
            </div>
          </div>
          {isFetchingGetInvoiceByInvoiceNumber ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner className="size-12" />
            </div>
          ) : (
            <div className="flex flex-col gap-6 p-6 pt-8">
              <div className="flex flex-col gap-5">
                {tabs.length > 1 && (
                  <FormTabs<
                    CollectPastDueInvoiceFormValues,
                    TabsCollectPastDueInvoiceType
                  >
                    name="activeTabId"
                    tabs={tabs}
                    className="justify-evenly gap-3 border-b-gray-300 pb-5"
                    tabClassName="py-2.5 px-3 rounded-xl max-w-none w-full items-center justify-center bg-gray-0 border border-gray-200"
                    activeTabClassName="bg-secondary-25 border border-secondary-700"
                    activeTabLabelClassName="text-secondary-700"
                  />
                )}
                <div ref={parent}>
                  {activeTabId === "ONLINE" ? (
                    <FormPaymentMethodPicker<CollectPastDueInvoiceFormValues>
                      name="paymentMethodId"
                      userMemberId={userMemberId}
                      customLabel={t(
                        "components.collectPastDueInvoiceSidebar.formPaymentMethodPicker.customLabel",
                      )}
                      allowBankPayment
                    />
                  ) : (
                    <FormSelectCustomPaymentCategory<CollectPastDueInvoiceFormValues>
                      name="inPersonPaymentMethod"
                      label={t(
                        "components.collectPastDueInvoiceSidebar.formCustomPaymentCategorySelectInput.label",
                      )}
                      labelClassName="text-base"
                    />
                  )}
                </div>
              </div>
              <DashedIcon />
              <div className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <div className="text-xl font-bold text-gray-950">
                    {t("components.collectPastDueInvoiceSidebar.amountDue")}
                  </div>
                  <div className="text-xl font-bold text-gray-950">
                    {formatCurrency(dueAmount, club.defaultCurrency)}
                  </div>
                </div>
              </div>
              {inPersonPaymentMethod?.value.name === "Cash" &&
                activeTabId === "IN_PERSON" && (
                  <ChangeCalculator price={dueAmount} />
                )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-t-gray-300 p-6">
          <Button
            intent="default"
            className="w-full"
            onClick={() => onCloseSidebar()}
          >
            {t("components.collectPastDueInvoiceSidebar.button.cancel")}
          </Button>
          <Button
            intent="secondary"
            className="w-full"
            disabled={isFetchingGetInvoiceByInvoiceNumber}
            onClick={async () => await formik.submitForm()}
          >
            {t("components.collectPastDueInvoiceSidebar.button.takePayment")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
